import { z } from "zod";
import {
  ActionSchema,
  CyclePhaseType,
  FileTypes,
  LogCheckAnomaliesSchema,
  LogCheckResultSchema,
  LogDocumentationSchema,
  LogExploreSchema,
  LogReflectSchema,
  ReflectDecisionSchema,
} from "@/lib/api/schemas/shared";
import { DateTimeWithOffset, FalsyBool, NullishDateTimeWithOffset, TruthyBool } from "@/lib/api/schemas/patterns";

// Mirrors the backend `State` enum (CycleOut.state); tolerant of new values.
export const CycleStateSchema = z
  .enum([
    "unknown",
    "new",
    "waiting",
    "running",
    "success",
    "failure",
    "warning",
    "error",
    "skip",
    "anomalies_detected",
    "cancelled",
  ])
  .catch("unknown");

export const ProcessErrorSchema = z.object({
  type: z.enum([
    "expected_result_failure",
    "anomalies_detected",
    "no_path_saved",
    "no_next_step",
    "performing_action",
    "consecutive_parsing_response",
    "max_rounds_reached",
    "failure_check_result",
    "unknown",
    "label_out_of_range",
    "parsing_response",
    "no_steps_found",
    "upload",
  ]),
  message: z.string(),
});

/**
 * Converts nested timestamps from seconds to milliseconds and normalizes entries.
 */
export const TimestampsSchema = z
  .object({
    type: z.enum(["start", "action", "finish"]),
    timestamp: z.number(),
  })
  .array()
  .array()
  .transform((rows) => {
    const out: { start: number; action: number; finish: number }[] = [];
    for (const row of rows) {
      const acc = { start: 0, action: 0, finish: 0 };
      for (const { type, timestamp: seconds } of row) acc[type] = Math.floor(seconds * 1000);
      out.push(acc);
    }
    return out;
  });

const BegTaskData = z.object({
  phase: CyclePhaseType,
  execution_start: DateTimeWithOffset.optional(),
  config: z.record(z.string(), z.any()).optional(),
  is_precondition: z.boolean().optional(),
  precondition_for_phase: CyclePhaseType.nullish(),
  error: z
    .object({
      type: z.string().optional(),
      message: z.string().optional(),
    })
    .nullable()
    .optional(),
});

const LearnStepData = z
  .object({
    img_before: z.string().nullable(),
    action: ActionSchema,
    round: z.number(),
    img_after: z.string().nullish(),
    run_action_success: FalsyBool,
    img_before_labeled: z.string().nullish(),
    log_action: z.string().nullish(),
    round_finish: NullishDateTimeWithOffset,
    error: ProcessErrorSchema.nullable(),
    img_after_labeled: z.string().nullish(),
    log_explore: LogExploreSchema.nullish(),
    reflect_decision: ReflectDecisionSchema.nullish(),
    log_reflect: LogReflectSchema.nullish(),
    log_generate_docs: LogDocumentationSchema.nullish(),
  })
  .transform((v) => ({
    ...v,
    img_before_labelled: v.img_before_labeled,
    img_after_labelled: v.img_after_labeled,
  }));

const RunStepData = z
  .object({
    img_before: z.string().nullable(),
    action: ActionSchema,
    round: z.number(),
    img_after: z.string().nullish(),
    run_action_success: FalsyBool,
    img_before_labeled: z.string().nullish(),
    log_action: z.string().nullish(),
    round_finish: NullishDateTimeWithOffset,
    error: ProcessErrorSchema.nullable(),
    check_anomalies_success: TruthyBool,
    img_check_anomalies_mockup: z.string().nullish(),
    log_check_anomalies: LogCheckAnomaliesSchema.nullish(),
  })
  .transform((v) => ({
    ...v,
    img_before_labelled: v.img_before_labeled,
  }));

export const EndStepData = z.union([LearnStepData, RunStepData]);

const LearnTaskEndData = z.object({
  expected_result_success: z.boolean().nullish(),
  log_expected_result: LogCheckResultSchema.nullish(),
  error: ProcessErrorSchema.nullable(),
  logs: z.string().nullish(),
  screen_record: z.string().nullish(),
  screen_record_timestamps: TimestampsSchema,
  is_path_saved: FalsyBool,
  log_path_saving: z.string().nullish(),
});

const RunTaskEndData = z.object({
  expected_result_success: z.boolean().nullish(),
  log_expected_result: LogCheckResultSchema.nullish(),
  error: ProcessErrorSchema.nullable(),
  logs: z.string().nullish(),
  screen_record: z.string().nullish(),
  screen_record_timestamps: TimestampsSchema,
  anomalies_detected: FalsyBool,
});

const StepData = z
  .string()
  .transform((v) => JSON.parse(v))
  .transform((v) => {
    const data = typeof v.data === "string" ? JSON.parse(v.data) : null;
    let variant = v.type;
    if (v.type === "end_step" || v.type === "end_task") {
      if (["learn", "relearn"].includes(v.phase)) {
        variant = `learn_${v.type}`;
      } else {
        variant = `run_${v.type}`;
      }
    }
    const { type, ...rest } = v;
    return { ...rest, data, variant };
  })
  .pipe(
    z.discriminatedUnion("variant", [
      z.object({
        variant: z.literal("beg_task"),
        phase: CyclePhaseType,
        data: BegTaskData,
      }),
      z.object({
        variant: z.literal("learn_end_step"),
        phase: z.enum(["learn", "relearn"]),
        data: LearnStepData,
      }),
      z.object({
        variant: z.literal("run_end_step"),
        phase: z.enum(["run", "run_relearn", "rerun"]),
        data: RunStepData,
      }),
      z.object({
        variant: z.literal("learn_end_task"),
        phase: z.enum(["learn", "relearn"]),
        data: LearnTaskEndData,
      }),
      z.object({
        variant: z.literal("run_end_task"),
        phase: z.enum(["run", "run_relearn", "rerun"]),
        data: RunTaskEndData,
      }),
    ]),
  );

const StepEntry = z.object({
  id: z.number(),
  created_at: DateTimeWithOffset,
  data: StepData,
  files: z
    .object({
      id: z.number(),
      created_at: DateTimeWithOffset,
      category: FileTypes,
      filename: z.string(),
      public_url: z.string().nullable(),
    })
    .array(),
});

// Skip steps that don't match a known variant (e.g. new-schema steps) instead of
// failing the whole cycle parse.
export const StepsSchema = z.array(z.unknown()).transform((steps) =>
  steps.flatMap((step) => {
    const parsed = StepEntry.safeParse(step);
    return parsed.success ? [parsed.data] : [];
  }),
);
